import { Component, Prop, State, Element, Host, h, Build } from '@stencil/core';
import {
  SIGNATURE_FONTS,
  SnapshotHistory,
  alphaBounds,
  clampRect,
  inkToAlpha,
  moveRect,
  rectFromPoints,
  resizeRect,
  validateFile,
  type CropHandle,
  type Rect,
} from './lib/signature';
import { trackFreeTool, type FreeToolStep } from './lib/analytics';
import {
  IconChevronDown,
  IconClear,
  IconContrast,
  IconCrop,
  IconRedo,
  IconRotate,
  IconRotateRight,
  IconUndo,
  IconUploadImage,
  IconZoomIn,
  IconZoomOut,
  IllustrationFileDownload,
  IllustrationFileLocked,
  IllustrationUploadSignature,
} from './icons';

const COMPONENT_NAME = 'sn-free-tool';

type Tab = 'type' | 'draw' | 'upload';
type Crop = 'original' | 'square';
type Point = [number, number];
/** Strokes are stored in canvas-relative units (0…1) so a resize keeps the drawing. */
type Stroke = Point[];

const TABS: readonly Tab[] = ['type', 'draw', 'upload'];
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif'];
const IMAGE_MAX_BYTES = 12 * 1024 * 1024;
const PDF_TYPES = ['application/pdf'];
const PDF_MAX_BYTES = 50 * 1024 * 1024;
/** Longest side an uploaded photo is reduced to before pixel work; keeps the sliders responsive. */
const IMAGE_WORK_SIZE = 1600;
const INK_COLOR = '#2f373e'; // TOKEN-GAP(signnow): canvas ink, same value as --color-neutral-neutral-darkest
const INK_WIDTH = 2.5;
const EXPORT_FONT_PX = 120;
const EXPORT_PADDING = 12;
const DOWNLOAD_NAME = 'SignNow Signature.png';
/** Smallest crop box, in preview CSS pixels; also the threshold below which a new drag is a click. */
const CROP_MIN = 24;
const CROP_HANDLES: readonly CropHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

/**
 * Free on-page signature tool for SignNow /esign/ pages: a Signature Generator or a document-signing entry point, no account needed.
 * Centered heading over a white tool card with Type / Draw / Upload tabs (tool A), or a two-column card with copy and a PDF drop zone (tool B); single column on mobile.
 * Everything in tool A runs in the browser and downloads a transparent PNG; tool B hands the PDF to the SignNow "sign my own document" flow and can show a free-limit state instead.
 *
 * @product signnow
 * @version 1.0.0
 * @category SignNow
 *
 * @slot heading - {required}, <me-heading variant="1" tag=["h1", "h2"]> - Tool heading; use h2 when the page hero already carries the h1
 * @slot description - <me-text tag="p"> - Short explanation under the heading
 * @slot features - {multiple}, <me-text tag="p"> - Tool B only: checklist items shown under the description
 *
 * @example
 *   <sn-free-tool tool="A">
 *     <me-heading slot="heading" variant="1" tag="h2">Create a free signature online</me-heading>
 *     <me-text slot="description" tag="p">Type, draw, or upload your signature. Download it as a transparent PNG — no account, no document needed.</me-text>
 *   </sn-free-tool>
 */
@Component({
  tag: 'sn-free-tool',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnFreeTool {
  @Element() host!: HTMLElement;

  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Which tool the block renders: "A" — Signature Generator, "B" — Sign a document online
   * @type {"A" | "B"}
   * @default A
   */
  @Prop({ reflect: true }) tool: 'A' | 'B' = 'A';

  /**
   * Show this block on the page. Switch off in the editor sidebar for pages of a dynamic template
   * that should not carry it; when `false` the block is hidden and no analytics fire.
   * @default true
   */
  @Prop({ attribute: 'show-block' }) showBlock: boolean = true;

  /**
   * Label of the Type tab
   * @content
   * @default Type
   */
  @Prop() tabTypeText: string = 'Type';

  /**
   * Label of the Draw tab
   * @content
   * @default Draw
   */
  @Prop() tabDrawText: string = 'Draw';

  /**
   * Label of the Upload tab
   * @content
   * @default Upload
   */
  @Prop() tabUploadText: string = 'Upload';

  /**
   * Heading of the Type tab
   * @content
   * @default Type Your Signature and Initials
   */
  @Prop() typeTitleText: string = 'Type Your Signature and Initials';

  /**
   * Placeholder of the name field
   * @content
   * @default Your full name
   */
  @Prop() typePlaceholderText: string = 'Your full name';

  /**
   * Sample text shown in the font previews until a name is typed
   * @content
   * @default Full name
   */
  @Prop() typeSampleText: string = 'Full name';

  /**
   * Heading of the Draw tab
   * @content
   * @default Draw Signature
   */
  @Prop() drawTitleText: string = 'Draw Signature';

  /**
   * Hint under the Draw heading
   * @content
   * @default Use the cursor to draw your signature.
   */
  @Prop() drawHintText: string = 'Use the cursor to draw your signature.';

  /**
   * Tooltip of the undo button
   * @content
   * @default Undo last action
   */
  @Prop() undoText: string = 'Undo last action';

  /**
   * Tooltip of the redo button
   * @content
   * @default Redo last action
   */
  @Prop() redoText: string = 'Redo last action';

  /**
   * Label of the clear button
   * @content
   * @default Clear
   */
  @Prop() clearText: string = 'Clear';

  /**
   * Heading of the empty Upload tab
   * @content
   * @default Upload or Drag & Drop Your Signature
   */
  @Prop() uploadTitleText: string = 'Upload or Drag & Drop Your Signature';

  /**
   * Accepted formats and size limit shown in the empty Upload tab
   * @content
   * @default Upload an image of your handwritten signature in JPG, GIF or PNG formats. The image you upload must be less than 12 MB.
   */
  @Prop() uploadHintText: string = 'Upload an image of your handwritten signature in JPG, GIF or PNG formats. The image you upload must be less than 12 MB.';

  /**
   * Label of the image picker button
   * @content
   * @default Upload Image
   */
  @Prop() uploadButtonText: string = 'Upload Image';

  /**
   * Error shown when the picked image has the wrong format or is too large
   * @content
   * @default Please choose a JPG, GIF or PNG image under 12 MB.
   */
  @Prop() uploadErrorText: string = 'Please choose a JPG, GIF or PNG image under 12 MB.';

  /**
   * Tooltip of the contrast slider
   * @content
   * @default Contrast
   */
  @Prop() contrastText: string = 'Contrast';

  /**
   * Tooltip of the rotate slider
   * @content
   * @default Rotate
   */
  @Prop() rotateText: string = 'Rotate';

  /**
   * Tooltip of the 90° rotate button
   * @content
   * @default Rotate right
   */
  @Prop() rotateRightText: string = 'Rotate right';

  /**
   * Tooltip of the crop selector
   * @content
   * @default Crop
   */
  @Prop() cropText: string = 'Crop';

  /**
   * Crop option that keeps the whole image
   * @content
   * @default Original
   */
  @Prop() cropOriginalText: string = 'Original';

  /**
   * Crop option that cuts a square
   * @content
   * @default Square
   */
  @Prop() cropSquareText: string = 'Square';

  /**
   * Tooltip of the zoom-in control
   * @content
   * @default Zoom in
   */
  @Prop() zoomInText: string = 'Zoom in';

  /**
   * Tooltip of the zoom-out control
   * @content
   * @default Zoom out
   */
  @Prop() zoomOutText: string = 'Zoom out';

  /**
   * Label of the download button of tool A
   * @content
   * @default Download signature
   */
  @Prop() downloadText: string = 'Download signature';

  /**
   * Tool B: drop zone headline
   * @content
   * @default Drag and drop document here to upload
   */
  @Prop() dropText: string = 'Drag and drop document here to upload';

  /**
   * Tool B: separator between drop and pick
   * @content
   * @default or
   */
  @Prop() orText: string = 'or';

  /**
   * Tool B: label of the file picker button
   * @content
   * @default Select from device
   */
  @Prop() selectText: string = 'Select from device';

  /**
   * Tool B: size limit note
   * @content
   * @default Upload files no larger than 50 MB
   */
  @Prop() sizeHintText: string = 'Upload files no larger than 50 MB';

  /**
   * Tool B: error shown for a non-PDF or too large file
   * @content
   * @default Please choose a PDF document no larger than 50 MB.
   */
  @Prop() documentErrorText: string = 'Please choose a PDF document no larger than 50 MB.';

  /**
   * Tool B: label shown while the document is being sent
   * @content
   * @default Uploading…
   */
  @Prop() uploadingText: string = 'Uploading…';

  /**
   * Tool B: endpoint of the SignNow "sign my own document" flow the PDF is posted to
   * @default /send-doc-to-sign/sign-my-own-doc
   */
  @Prop() signEndpoint: string = '/send-doc-to-sign/sign-my-own-doc';

  /**
   * Tool B: show the free-limit state instead of the drop zone
   * @default false
   */
  @Prop() limitReached: boolean = false;

  /**
   * Tool B: heading of the free-limit state
   * @content
   * @default Free limit reached
   */
  @Prop() limitTitleText: string = 'Free limit reached';

  /**
   * Tool B: explanation of the free-limit state
   * @content
   * @default You've signed 3 of 3 free documents this month.
   */
  @Prop() limitText: string = "You've signed 3 of 3 free documents this month.";

  /**
   * Tool B: label of the pricing link in the free-limit state
   * @content
   * @default View pricing
   */
  @Prop() pricingText: string = 'View pricing';

  /**
   * Tool B: URL of the pricing link
   * @default /pricing
   */
  @Prop() pricingHref: string = '/pricing';

  /**
   * Tool B: label of the trial link in the free-limit state
   * @content
   * @default Continue with free trial
   */
  @Prop() trialText: string = 'Continue with free trial';

  /**
   * Tool B: URL of the trial link
   * @default /register
   */
  @Prop() trialHref: string = '/register';

  @State() tab: Tab = 'type';
  @State() name = '';
  @State() fontIndex = 0;
  @State() strokeCount = 0;
  @State() canUndo = false;
  @State() canRedo = false;
  @State() hasImage = false;
  @State() contrast = 50;
  @State() rotation = 0;
  @State() crop: Crop = 'original';
  @State() zoom = 1;
  /** Crop box in preview CSS pixels; null until the visitor draws one (a centred square is shown). */
  @State() cropRect: Rect | null = null;
  @State() dragging = false;
  @State() error = '';
  @State() submitting = false;

  private history = new SnapshotHistory<Stroke[]>([]);
  private liveStroke: Stroke | null = null;
  private drawCanvas?: HTMLCanvasElement;
  private previewCanvas?: HTMLCanvasElement;
  private imageInput?: HTMLInputElement;
  private documentInput?: HTMLInputElement;
  /** The uploaded photo after background removal, at working size. */
  private inkCanvas?: HTMLCanvasElement;
  private sourcePixels?: ImageData;
  private resizeObserver?: ResizeObserver;
  private tracked = new Set<FreeToolStep>();
  private cropDrag: { mode: 'create' | 'move' | CropHandle; x: number; y: number; start: Rect; pointerId: number } | null = null;

  componentDidLoad() {
    if (!Build.isBrowser) return;
    if (this.showBlock && this.tool === 'B' && this.limitReached) this.track('limit_reached');
    this.resizeObserver = new ResizeObserver(() => { this.fitCanvases(); });
    this.resizeObserver.observe(this.host);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  componentDidRender() {
    if (!Build.isBrowser) return;
    this.fitCanvases();
  }

  private track(step: FreeToolStep, detail?: string) {
    // Usage steps count once per page view; download and CTA clicks every time.
    const once = step !== 'download_click' && step !== 'cta_to_product_click';
    if (once && this.tracked.has(step)) return;
    this.tracked.add(step);
    trackFreeTool(step, this.tool, detail);
  }

  private start = () => { this.track('tool_start'); };

  // ---------------------------------------------------------------- tabs

  private selectTab(tab: Tab, focus = false) {
    this.tab = tab;
    this.error = '';
    if (focus) this.host.querySelector<HTMLElement>(`#${COMPONENT_NAME}-tab-${tab}`)?.focus();
  }

  private onTabKeyDown = (event: KeyboardEvent) => {
    const index = TABS.indexOf(this.tab);
    const next =
      event.key === 'ArrowRight' ? TABS[(index + 1) % TABS.length]
      : event.key === 'ArrowLeft' ? TABS[(index + TABS.length - 1) % TABS.length]
      : event.key === 'Home' ? TABS[0]
      : event.key === 'End' ? TABS[TABS.length - 1]
      : null;
    if (!next) return;
    event.preventDefault();
    this.selectTab(next, true);
  };

  // ---------------------------------------------------------------- canvases

  /** Keeps each canvas backing store at its displayed size × devicePixelRatio, then repaints. */
  private fitCanvases() {
    for (const canvas of [this.drawCanvas, this.previewCanvas]) {
      if (!canvas || !canvas.isConnected) continue;
      const ratio = window.devicePixelRatio || 1;
      const width = Math.round(canvas.clientWidth * ratio);
      const height = Math.round(canvas.clientHeight * ratio);
      if (width && height && (canvas.width !== width || canvas.height !== height)) {
        canvas.width = width;
        canvas.height = height;
      }
    }
    this.paintDrawing();
    this.paintPreview();
  }

  private paintStrokes(ctx: CanvasRenderingContext2D, strokes: Stroke[], width: number, height: number, lineWidth: number) {
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = INK_COLOR;
    ctx.fillStyle = INK_COLOR;
    ctx.lineWidth = lineWidth;
    for (const stroke of strokes) {
      const points = stroke.map(([x, y]) => [x * width, y * height] as Point);
      if (points.length === 1) {
        ctx.beginPath();
        ctx.arc(points[0][0], points[0][1], lineWidth / 2, 0, Math.PI * 2);
        ctx.fill();
        continue;
      }
      // Quadratic curves through midpoints: smooth lines without a spline library.
      ctx.beginPath();
      ctx.moveTo(points[0][0], points[0][1]);
      for (let i = 1; i < points.length - 1; i++) {
        const [x, y] = points[i];
        const [nx, ny] = points[i + 1];
        ctx.quadraticCurveTo(x, y, (x + nx) / 2, (y + ny) / 2);
      }
      const last = points[points.length - 1];
      ctx.lineTo(last[0], last[1]);
      ctx.stroke();
    }
  }

  private paintDrawing() {
    const canvas = this.drawCanvas;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const strokes = this.liveStroke ? [...this.history.value, this.liveStroke] : this.history.value;
    this.paintStrokes(ctx, strokes, canvas.width, canvas.height, INK_WIDTH * (window.devicePixelRatio || 1));
  }

  private pointFrom(event: PointerEvent): Point {
    const rect = this.drawCanvas!.getBoundingClientRect();
    return [(event.clientX - rect.left) / rect.width, (event.clientY - rect.top) / rect.height];
  }

  private onPointerDown = (event: PointerEvent) => {
    if (!this.drawCanvas || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.preventDefault();
    this.start();
    // Capture keeps the stroke going when the pointer leaves the canvas; drawing must not depend on it.
    try { this.drawCanvas.setPointerCapture(event.pointerId); } catch { /* pointer already gone */ }
    this.liveStroke = [this.pointFrom(event)];
    this.paintDrawing();
  };

  private onPointerMove = (event: PointerEvent) => {
    if (!this.liveStroke) return;
    this.liveStroke.push(this.pointFrom(event));
    this.paintDrawing();
  };

  private onPointerUp = () => {
    if (!this.liveStroke) return;
    this.history.push([...this.history.value, this.liveStroke]);
    this.liveStroke = null;
    this.track('draw_used');
    this.syncHistory();
  };

  private syncHistory() {
    this.strokeCount = this.history.value.length;
    this.canUndo = this.history.canUndo;
    this.canRedo = this.history.canRedo;
    this.paintDrawing();
  }

  private undo = () => { this.history.undo(); this.syncHistory(); };
  private redo = () => { this.history.redo(); this.syncHistory(); };
  private clear = () => {
    if (!this.history.value.length) return;
    this.history.push([]);
    this.syncHistory();
  };

  // ---------------------------------------------------------------- upload (tool A)

  private pickImage = () => { this.start(); this.imageInput?.click(); };

  private onImageChosen = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) void this.loadImage(file);
  };

  private async loadImage(file: File) {
    if (validateFile(file, IMAGE_TYPES, IMAGE_MAX_BYTES)) {
      this.error = this.uploadErrorText;
      return;
    }
    this.error = '';
    let bitmap: ImageBitmap;
    try {
      bitmap = await createImageBitmap(file);
    } catch {
      this.error = this.uploadErrorText;
      return;
    }
    const scale = Math.min(1, IMAGE_WORK_SIZE / Math.max(bitmap.width, bitmap.height));
    const work = document.createElement('canvas');
    work.width = Math.max(1, Math.round(bitmap.width * scale));
    work.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = work.getContext('2d', { willReadFrequently: true })!;
    ctx.drawImage(bitmap, 0, 0, work.width, work.height);
    bitmap.close();
    this.sourcePixels = ctx.getImageData(0, 0, work.width, work.height);
    this.inkCanvas = work;
    this.contrast = 50;
    this.rotation = 0;
    this.crop = 'original';
    this.zoom = 1;
    this.cropRect = null;
    this.applyContrast();
    this.hasImage = true;
    this.track('upload_used');
  }

  private applyContrast() {
    if (!this.sourcePixels || !this.inkCanvas) return;
    const { width, height, data } = this.sourcePixels;
    const out = new ImageData(inkToAlpha(data, this.contrast), width, height);
    this.inkCanvas.getContext('2d')!.putImageData(out, 0, 0);
    this.paintPreview();
  }

  /**
   * The upload scene in CSS pixels of the preview: the image fitted with a margin and rotated about
   * the centre. In crop mode the zoom slider scales it further under the crop box.
   */
  private sceneLayout(width: number, height: number) {
    const image = this.inkCanvas!;
    const margin = 24;
    const angle = (this.rotation * Math.PI) / 180;
    const cos = Math.abs(Math.cos(angle));
    const sin = Math.abs(Math.sin(angle));
    const boxW = image.width * cos + image.height * sin;
    const boxH = image.width * sin + image.height * cos;
    const fit = Math.min((width - margin * 2) / boxW, (height - margin * 2) / boxH);
    return { angle, scale: this.crop === 'square' ? fit * this.zoom : fit };
  }

  /** The crop box to use: the visitor's, fitted to the current preview size, or a centred square. */
  private currentCrop(width: number, height: number): Rect {
    if (this.cropRect) return clampRect(this.cropRect, width, height, CROP_MIN);
    const side = Math.max(CROP_MIN, Math.min(width, height) - 48);
    return { x: (width - side) / 2, y: (height - side) / 2, width: side, height: side };
  }

  private drawScene(ctx: CanvasRenderingContext2D, width: number, height: number, alpha = 1) {
    const image = this.inkCanvas!;
    const { angle, scale } = this.sceneLayout(width, height);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(width / 2, height / 2);
    ctx.rotate(angle);
    ctx.scale(scale, scale);
    ctx.drawImage(image, -image.width / 2, -image.height / 2);
    ctx.restore();
  }

  private paintPreview() {
    const canvas = this.previewCanvas;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !this.inkCanvas) return;
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.width / ratio;
    const height = canvas.height / ratio;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, width, height);
    if (this.crop === 'square') {
      // Outside the box the image is dimmed; the box itself (border, handles) is DOM on top.
      const box = this.currentCrop(width, height);
      this.drawScene(ctx, width, height, 0.35);
      ctx.save();
      ctx.beginPath();
      ctx.rect(box.x, box.y, box.width, box.height);
      ctx.clip();
      ctx.clearRect(box.x, box.y, box.width, box.height);
      this.drawScene(ctx, width, height);
      ctx.restore();
    } else {
      this.drawScene(ctx, width, height);
    }
  }

  private onContrast = (event: Event) => { this.contrast = Number((event.target as HTMLInputElement).value); this.applyContrast(); };
  private onRotation = (event: Event) => { this.rotation = Number((event.target as HTMLInputElement).value); };
  private rotateRight = () => { this.rotation = ((this.rotation + 90 + 180) % 360) - 180; };
  private onCrop = (event: Event) => {
    this.crop = (event.target as HTMLSelectElement).value as Crop;
    this.zoom = 1;
    this.cropRect = null;
  };

  // ---------------------------------------------------------------- crop box (tool A upload)

  private previewPoint(event: PointerEvent): [number, number] {
    const rect = this.previewCanvas!.getBoundingClientRect();
    return [event.clientX - rect.left, event.clientY - rect.top];
  }

  private previewSize(): [number, number] {
    return [this.previewCanvas!.clientWidth, this.previewCanvas!.clientHeight];
  }

  /**
   * One pointer handler for the whole crop area, like selecting on a desktop: press outside the box
   * and drag to draw a new one, press inside to move it, press a handle to resize from that side.
   */
  private onCropPointerDown = (event: PointerEvent) => {
    if (!this.previewCanvas || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.preventDefault();
    const [width, height] = this.previewSize();
    const [x, y] = this.previewPoint(event);
    const target = event.target as HTMLElement;
    const handle = target.dataset.handle as CropHandle | undefined;
    const start = this.currentCrop(width, height);
    const inside = x >= start.x && x <= start.x + start.width && y >= start.y && y <= start.y + start.height;
    const mode = handle ?? (inside ? 'move' : 'create');
    this.cropDrag = { mode, x, y, start, pointerId: event.pointerId };
    try { (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId); } catch { /* pointer already gone */ }
  };

  private onCropPointerMove = (event: PointerEvent) => {
    const drag = this.cropDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    const [width, height] = this.previewSize();
    const [x, y] = this.previewPoint(event);
    const dx = x - drag.x;
    const dy = y - drag.y;
    if (drag.mode === 'create') {
      // Below the minimum the drag is still a click; keep the old box until it becomes a real one.
      if (Math.abs(dx) < CROP_MIN && Math.abs(dy) < CROP_MIN) return;
      this.cropRect = rectFromPoints(drag.x, drag.y, x, y, width, height);
    } else if (drag.mode === 'move') {
      this.cropRect = moveRect(drag.start, dx, dy, width, height);
    } else {
      this.cropRect = resizeRect(drag.start, drag.mode, dx, dy, width, height, CROP_MIN);
    }
  };

  private onCropPointerUp = () => {
    if (this.cropRect && (this.cropRect.width < CROP_MIN || this.cropRect.height < CROP_MIN)) {
      this.cropRect = clampRect(this.cropRect, ...this.previewSize(), CROP_MIN);
    }
    this.cropDrag = null;
  };

  /** Arrow keys move the focused box by 4px, 16px with Shift; with Alt they resize it instead. */
  private onCropKeyDown = (event: KeyboardEvent) => {
    const steps: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const step = steps[event.key];
    if (!step || !this.previewCanvas) return;
    event.preventDefault();
    const [width, height] = this.previewSize();
    const distance = event.shiftKey ? 16 : 4;
    const box = this.currentCrop(width, height);
    this.cropRect = event.altKey
      ? resizeRect(box, step[0] ? 'e' : 's', step[0] * distance, step[1] * distance, width, height, CROP_MIN)
      : moveRect(box, step[0] * distance, step[1] * distance, width, height);
  };
  private onZoom = (event: Event) => { this.zoom = Number((event.target as HTMLInputElement).value); };
  private stepZoom(delta: number) { this.zoom = Math.min(3, Math.max(1, Math.round((this.zoom + delta) * 10) / 10)); }

  // ---------------------------------------------------------------- drag & drop (both tools)

  private onDragOver = (event: DragEvent) => {
    event.preventDefault();
    this.dragging = true;
  };

  private onDragLeave = (event: DragEvent) => {
    if (!(event.currentTarget as HTMLElement).contains(event.relatedTarget as Node)) this.dragging = false;
  };

  private onDrop = (event: DragEvent) => {
    event.preventDefault();
    this.dragging = false;
    this.start();
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    if (this.tool === 'B') this.sendDocument(file);
    else void this.loadImage(file);
  };

  // ---------------------------------------------------------------- export (tool A)

  private readyToDownload(): boolean {
    if (this.tab === 'type') return this.name.trim().length > 0;
    if (this.tab === 'draw') return this.strokeCount > 0;
    return this.hasImage;
  }

  private async renderSignature(): Promise<HTMLCanvasElement | null> {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

    if (this.tab === 'type') {
      const { family } = SIGNATURE_FONTS[this.fontIndex];
      const font = `${EXPORT_FONT_PX}px "${family}"`;
      await document.fonts.load(font, this.name);
      ctx.font = font;
      canvas.width = Math.ceil(ctx.measureText(this.name).width + EXPORT_FONT_PX);
      canvas.height = Math.ceil(EXPORT_FONT_PX * 2);
      ctx.font = font; // resizing the canvas resets the context
      ctx.fillStyle = '#000000';
      ctx.textBaseline = 'middle';
      ctx.fillText(this.name, EXPORT_FONT_PX / 2, canvas.height / 2);
    } else if (this.tab === 'draw' && this.drawCanvas) {
      canvas.width = this.drawCanvas.clientWidth * 2;
      canvas.height = this.drawCanvas.clientHeight * 2;
      this.paintStrokes(ctx, this.history.value, canvas.width, canvas.height, INK_WIDTH * 2);
    } else if (this.tab === 'upload' && this.previewCanvas && this.inkCanvas) {
      // Re-render the preview scene at the photo's own resolution instead of scaling the preview up.
      const width = this.previewCanvas.clientWidth;
      const height = this.previewCanvas.clientHeight;
      const k = Math.min(4, Math.max(1, 1 / this.sceneLayout(width, height).scale));
      canvas.width = Math.round(width * k);
      canvas.height = Math.round(height * k);
      ctx.scale(k, k);
      this.drawScene(ctx, width, height);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      if (this.crop === 'square') {
        // A crop exports exactly the selected box, transparent corners included.
        const box = this.currentCrop(width, height);
        const out = document.createElement('canvas');
        out.width = Math.max(1, Math.round(box.width * k));
        out.height = Math.max(1, Math.round(box.height * k));
        out.getContext('2d')!.drawImage(canvas, box.x * k, box.y * k, out.width, out.height, 0, 0, out.width, out.height);
        return out;
      }
    } else {
      return null;
    }
    return this.trim(canvas);
  }

  /** Cuts the transparent margin, keeping a little padding; the PNG is just the signature. */
  private trim(canvas: HTMLCanvasElement): HTMLCanvasElement | null {
    const ctx = canvas.getContext('2d')!;
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const box = alphaBounds(data, canvas.width, canvas.height);
    if (!box) return null;
    const out = document.createElement('canvas');
    out.width = box.width + EXPORT_PADDING * 2;
    out.height = box.height + EXPORT_PADDING * 2;
    out.getContext('2d')!.drawImage(canvas, box.x, box.y, box.width, box.height, EXPORT_PADDING, EXPORT_PADDING, box.width, box.height);
    return out;
  }

  private download = async () => {
    if (!this.readyToDownload()) return;
    this.track('download_click', this.tab);
    const canvas = await this.renderSignature();
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = DOWNLOAD_NAME;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, 'image/png');
  };

  // ---------------------------------------------------------------- tool B

  private pickDocument = () => { this.start(); this.documentInput?.click(); };

  private onDocumentChosen = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) this.sendDocument(file);
    input.value = '';
  };

  /**
   * Posts the PDF to the same endpoint the signnow.com home page uses for "Sign my own document"
   * (multipart, Laravel CSRF token, `formName=ownDocument`); the response opens the editor.
   */
  private sendDocument(file: File) {
    if (validateFile(file, PDF_TYPES, PDF_MAX_BYTES)) {
      this.error = this.documentErrorText;
      return;
    }
    this.error = '';
    this.submitting = true;
    this.track('cta_to_product_click', 'upload');

    const form = document.createElement('form');
    form.method = 'post';
    form.enctype = 'multipart/form-data';
    form.action = this.signEndpoint;
    form.hidden = true;
    const field = (name: string, value: string) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };
    field('_token', document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '');
    field('formName', 'ownDocument');
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.name = 'ownDocument';
    const transfer = new DataTransfer();
    transfer.items.add(file);
    fileInput.files = transfer.files;
    form.appendChild(fileInput);
    document.body.appendChild(form);
    form.submit();
  }

  // ---------------------------------------------------------------- render

  private renderTabs() {
    const labels: Record<Tab, string> = { type: this.tabTypeText, draw: this.tabDrawText, upload: this.tabUploadText };
    return (
      <div class={`${COMPONENT_NAME}__tabs`} role="tablist">
        {TABS.map((tab) => (
          <button
            type="button"
            role="tab"
            id={`${COMPONENT_NAME}-tab-${tab}`}
            aria-controls={`${COMPONENT_NAME}-panel-${tab}`}
            aria-selected={this.tab === tab ? 'true' : 'false'}
            tabIndex={this.tab === tab ? 0 : -1}
            onKeyDown={this.onTabKeyDown}
            class={{ [`${COMPONENT_NAME}__tab`]: true, [`${COMPONENT_NAME}__tab--active`]: this.tab === tab }}
            onClick={() => { this.start(); this.selectTab(tab); }}
          >
            {labels[tab]}
          </button>
        ))}
      </div>
    );
  }

  private renderTypePanel() {
    const text = this.name.trim() || this.typeSampleText;
    return (
      <div class={`${COMPONENT_NAME}__panel`}>
        <label class={`${COMPONENT_NAME}__label`} htmlFor={`${COMPONENT_NAME}-name`}>{this.typeTitleText}</label>
        <input
          id={`${COMPONENT_NAME}-name`}
          class={`${COMPONENT_NAME}__input`}
          type="text"
          autocomplete="name"
          maxLength={60}
          placeholder={this.typePlaceholderText}
          value={this.name}
          onFocus={this.start}
          onInput={(event) => { this.name = (event.target as HTMLInputElement).value; this.track('type_used'); }}
        />
        <div class={`${COMPONENT_NAME}__fonts`} role="radiogroup" aria-label={this.typeTitleText}>
          {SIGNATURE_FONTS.map((font, index) => (
            <button
              type="button"
              role="radio"
              aria-checked={this.fontIndex === index ? 'true' : 'false'}
              class={{ [`${COMPONENT_NAME}__font`]: true, [`${COMPONENT_NAME}__font--selected`]: this.fontIndex === index }}
              style={{ fontFamily: `"${font.family}", cursive`, fontSize: `calc(${font.size}px * var(--sn-free-tool-font-scale, 1))` }}
              onClick={() => { this.start(); this.fontIndex = index; }}
            >
              <span class={`${COMPONENT_NAME}__font-text`}>{text}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  private renderIconButton(label: string, icon: () => any, onClick: () => void, disabled = false) {
    return (
      <button type="button" class={`${COMPONENT_NAME}__icon-button`} aria-label={label} data-tooltip={label} disabled={disabled} onClick={onClick}>
        {icon()}
      </button>
    );
  }

  private renderDrawPanel() {
    return (
      <div class={`${COMPONENT_NAME}__panel`}>
        <div class={`${COMPONENT_NAME}__draw-head`}>
          <div>
            <p class={`${COMPONENT_NAME}__label`}>{this.drawTitleText}</p>
            <p class={`${COMPONENT_NAME}__hint`}>{this.drawHintText}</p>
          </div>
          <div class={`${COMPONENT_NAME}__draw-actions`}>
            {this.renderIconButton(this.undoText, IconUndo, this.undo, !this.canUndo)}
            {this.renderIconButton(this.redoText, IconRedo, this.redo, !this.canRedo)}
            <span class={`${COMPONENT_NAME}__divider`} aria-hidden="true"></span>
            <button type="button" class={`${COMPONENT_NAME}__clear`} disabled={!this.strokeCount} onClick={this.clear}>
              <IconClear />
              <span>{this.clearText}</span>
            </button>
          </div>
        </div>
        <div class={`${COMPONENT_NAME}__canvas-wrap`}>
          <canvas
            ref={(el) => { this.drawCanvas = el; }}
            class={`${COMPONENT_NAME}__canvas`}
            role="img"
            aria-label={this.drawHintText}
            onPointerDown={this.onPointerDown}
            onPointerMove={this.onPointerMove}
            onPointerUp={this.onPointerUp}
            onPointerCancel={this.onPointerUp}
          ></canvas>
          <span class={`${COMPONENT_NAME}__baseline`} aria-hidden="true"></span>
        </div>
      </div>
    );
  }

  private renderUploadPanel() {
    if (!this.hasImage) {
      return (
        <div class={`${COMPONENT_NAME}__panel`}>
          <div
            class={{ [`${COMPONENT_NAME}__dropzone`]: true, [`${COMPONENT_NAME}__dropzone--active`]: this.dragging }}
            onDragOver={this.onDragOver}
            onDragLeave={this.onDragLeave}
            onDrop={this.onDrop}
          >
            <IllustrationUploadSignature />
            <p class={`${COMPONENT_NAME}__drop-title`}>{this.uploadTitleText}</p>
            <p class={`${COMPONENT_NAME}__hint ${COMPONENT_NAME}__hint--center`}>{this.uploadHintText}</p>
            <button type="button" class={`${COMPONENT_NAME}__outline-button`} onClick={this.pickImage}>
              <IconUploadImage />
              <span>{this.uploadButtonText}</span>
            </button>
            {this.error && <p class={`${COMPONENT_NAME}__error`} role="alert">{this.error}</p>}
          </div>
          <input ref={(el) => { this.imageInput = el; }} type="file" accept={IMAGE_TYPES.join(',')} class={`${COMPONENT_NAME}__file`} tabIndex={-1} aria-hidden="true" onChange={this.onImageChosen} />
        </div>
      );
    }

    return (
      <div class={`${COMPONENT_NAME}__panel`}>
        <div class={`${COMPONENT_NAME}__editor-actions`}>
          <label class={`${COMPONENT_NAME}__slider`} data-tooltip={this.contrastText} style={{ '--fill': `${this.contrast}%` }}>
            <IconContrast />
            <input type="range" min="0" max="100" value={this.contrast} aria-label={this.contrastText} onInput={this.onContrast} />
          </label>
          <span class={`${COMPONENT_NAME}__divider`} aria-hidden="true"></span>
          <label class={`${COMPONENT_NAME}__slider ${COMPONENT_NAME}__slider--centered`} data-tooltip={this.rotateText}>
            <IconRotate />
            <span class={`${COMPONENT_NAME}__range`}>
              <input type="range" min="-180" max="180" value={this.rotation} aria-label={this.rotateText} onInput={this.onRotation} />
              <span class={`${COMPONENT_NAME}__range-tick`} aria-hidden="true"></span>
            </span>
          </label>
          {this.renderIconButton(this.rotateRightText, IconRotateRight, this.rotateRight)}
          <span class={`${COMPONENT_NAME}__divider`} aria-hidden="true"></span>
          <label class={`${COMPONENT_NAME}__select`} data-tooltip={this.cropText}>
            <IconCrop />
            <select aria-label={this.cropText} onChange={this.onCrop}>
              <option value="original" selected={this.crop === 'original'}>{this.cropOriginalText}</option>
              <option value="square" selected={this.crop === 'square'}>{this.cropSquareText}</option>
            </select>
            <IconChevronDown />
          </label>
        </div>
        <div class={`${COMPONENT_NAME}__preview`}>
          <canvas ref={(el) => { this.previewCanvas = el; }} class={`${COMPONENT_NAME}__canvas`} role="img" aria-label={this.uploadTitleText}></canvas>
          {this.crop === 'square' && this.renderCropBox()}
          {this.crop === 'square' && (
            <div class={`${COMPONENT_NAME}__zoom`}>
              {this.renderIconButton(this.zoomOutText, IconZoomOut, () => this.stepZoom(-0.2), this.zoom <= 1)}
              <input type="range" min="1" max="3" step="0.05" value={this.zoom} aria-label={this.zoomInText} onInput={this.onZoom} />
              {this.renderIconButton(this.zoomInText, IconZoomIn, () => this.stepZoom(0.2), this.zoom >= 3)}
            </div>
          )}
        </div>
      </div>
    );
  }

  private renderCropBox() {
    const [width, height] = this.previewCanvas ? this.previewSize() : [0, 0];
    const box = width && height ? this.currentCrop(width, height) : null;
    return (
      // Pointer-only surface; the box inside is the keyboard-operable control.
       
      <div
        class={`${COMPONENT_NAME}__crop-area`}
        onPointerDown={this.onCropPointerDown}
        onPointerMove={this.onCropPointerMove}
        onPointerUp={this.onCropPointerUp}
        onPointerCancel={this.onCropPointerUp}
      >
        {box && (
          <div
            class={`${COMPONENT_NAME}__crop-box`}
            style={{ left: `${box.x}px`, top: `${box.y}px`, width: `${box.width}px`, height: `${box.height}px` }}
            role="slider"
            tabIndex={0}
            aria-label={this.cropText}
            aria-valuemin={0}
            aria-valuemax={Math.round(width)}
            aria-valuenow={Math.round(box.x)}
            aria-valuetext={`${Math.round(box.width)} × ${Math.round(box.height)}`}
            onKeyDown={this.onCropKeyDown}
          >
            {CROP_HANDLES.map((handle) => (
              <span class={`${COMPONENT_NAME}__crop-handle ${COMPONENT_NAME}__crop-handle--${handle}`} data-handle={handle} aria-hidden="true"></span>
            ))}
          </div>
        )}
      </div>
    );
  }

  private renderToolA() {
    const panels: Record<Tab, () => any> = {
      type: () => this.renderTypePanel(),
      draw: () => this.renderDrawPanel(),
      upload: () => this.renderUploadPanel(),
    };
    // The empty Type tab has no footer in the design; every other state shows the button.
    const showFooter = this.tab !== 'type' || this.name.trim().length > 0;
    return (
      <div class={`${COMPONENT_NAME}__card`} onPointerDown={this.start}>
        <div class={`${COMPONENT_NAME}__card-head`}>{this.renderTabs()}</div>
        <div
          class={`${COMPONENT_NAME}__card-body`}
          role="tabpanel"
          id={`${COMPONENT_NAME}-panel-${this.tab}`}
          aria-labelledby={`${COMPONENT_NAME}-tab-${this.tab}`}
        >
          {panels[this.tab]()}
        </div>
        {showFooter && (
          <div class={`${COMPONENT_NAME}__card-foot`}>
            <me-button variant="primary" tag="button" disabled={!this.readyToDownload()} clickHandler={this.download}>
              {this.downloadText}
            </me-button>
          </div>
        )}
      </div>
    );
  }

  private renderToolB() {
    if (this.limitReached) {
      return (
        <div class={`${COMPONENT_NAME}__upload-card ${COMPONENT_NAME}__upload-card--limit`}>
          <IllustrationFileLocked />
          <p class={`${COMPONENT_NAME}__limit-title`}>{this.limitTitleText}</p>
          <p class={`${COMPONENT_NAME}__limit-text`}>{this.limitText}</p>
          <div class={`${COMPONENT_NAME}__limit-actions`}>
            <me-button variant="secondary" tag="a" href={this.pricingHref} clickHandler={() => this.track('cta_to_product_click', 'pricing')}>
              {this.pricingText}
            </me-button>
            <me-button variant="primary" tag="a" href={this.trialHref} clickHandler={() => this.track('cta_to_product_click', 'trial')}>
              {this.trialText}
            </me-button>
          </div>
        </div>
      );
    }

    return (
      <div
        class={{ [`${COMPONENT_NAME}__upload-card`]: true, [`${COMPONENT_NAME}__upload-card--active`]: this.dragging }}
        onDragOver={this.onDragOver}
        onDragLeave={this.onDragLeave}
        onDrop={this.onDrop}
      >
        <div class={`${COMPONENT_NAME}__upload-inner`}>
          <IllustrationFileDownload />
          <p class={`${COMPONENT_NAME}__drop-text`}>{this.dropText}</p>
          <p class={`${COMPONENT_NAME}__or`}><span>{this.orText}</span></p>
          <me-button variant="primary" tag="button" disabled={this.submitting} clickHandler={this.pickDocument}>
            {this.submitting ? this.uploadingText : this.selectText}
          </me-button>
          <p class={`${COMPONENT_NAME}__hint ${COMPONENT_NAME}__hint--center`}>{this.sizeHintText}</p>
          {this.error && <p class={`${COMPONENT_NAME}__error`} role="alert">{this.error}</p>}
        </div>
        <input ref={(el) => { this.documentInput = el; }} type="file" accept={PDF_TYPES.join(',')} class={`${COMPONENT_NAME}__file`} tabIndex={-1} aria-hidden="true" onChange={this.onDocumentChosen} />
      </div>
    );
  }

  render() {
    const isB = this.tool === 'B';
    // Switched off for this page the markup still renders and is only hidden: dropping it would
    // also drop the slots, and Stencil does not give slotted content back when the tool is switched
    // on again in the editor.
    return (
      <Host data-color-mode={this.colorMode} hidden={!this.showBlock}>
        <section class={{ [COMPONENT_NAME]: true, [`${COMPONENT_NAME}--sign`]: isB }}>
          <div class={`${COMPONENT_NAME}__container`}>
            <div class={`${COMPONENT_NAME}__intro`}>
              <div class={`${COMPONENT_NAME}__heading`}>
                <slot name="heading"></slot>
              </div>
              <div class={`${COMPONENT_NAME}__description`}>
                <slot name="description"></slot>
              </div>
              {isB && (
                <div class={`${COMPONENT_NAME}__features`}>
                  <slot name="features"></slot>
                </div>
              )}
            </div>
            {isB ? this.renderToolB() : this.renderToolA()}
          </div>
        </section>
      </Host>
    );
  }
}
